import { SchemaOf } from 'yup';
import * as Yup from 'yup';
import { Formik, FormikProps } from 'formik';
import {
  FormFieldsSection,
  FormLeftSection,
  FormRow,
  StyledForm,
} from 'app/components/Forms/FormsLayout';
import { FormTextField } from 'app/components/Forms/FormTextField';
import { useTranslation } from 'react-i18next';
import { translations } from 'locales/translations';
import { Button } from 'app/components/BasicButtons/Button';
import { MobileTabletVersion, UserPasswordRemember } from './type';
import React from 'react';
import ButtonLink from 'app/components/BasicLinks/ButtonLink';
import { useSelector } from 'react-redux';
import { selectAppSettings } from 'app/slice/selectors';
import { toRootedURL } from 'utils/url-utils';
import { FormRecaptcha } from 'app/components/Forms/FormRecaptcha';
import { selectBadAttempts } from '../slice/selectors';
import { BasicCheckBox } from 'app/components/BasicCheckBox';

export interface UserPasswordFormProps {
  initial: UserPasswordRemember;
  showRememberMe: boolean;
  onSubmit: (item: UserPasswordRemember) => void;
  SSOSubmit?: () => void;
  formRef: React.MutableRefObject<FormikProps<UserPasswordRemember> | null>;
  submitting?: boolean;
  mobileTabletVersion?: MobileTabletVersion;
}

export function UserPasswordForm(props: UserPasswordFormProps) {
  const {
    initial,
    formRef,
    onSubmit,
    submitting,
    showRememberMe,
    SSOSubmit,
  } = props;
  const { t } = useTranslation();
  const appSettings = useSelector(selectAppSettings);
  const loginBadAttempts = useSelector(selectBadAttempts);
  const [rememberMe, setRememberMe] = React.useState<boolean>(true);
  const schema: SchemaOf<UserPasswordRemember> = React.useMemo(() => {
    let result: SchemaOf<UserPasswordRemember>;
    if (loginBadAttempts >= 3) {
      result = Yup.object({
        UserName: Yup.string()
          .required()
          .label(
            props.mobileTabletVersion !== undefined ? 'Email' : 'UserName',
          ),
        Password: Yup.string().required(),
        RememberMe: Yup.boolean().default(initial.RememberMe),
        Recaptcha: Yup.string()
          .label(t(translations.Recaptcha) as string)
          .required(t(translations.reg_CaptchaRequired) as string)
          .nullable(true),
      });
      return result;
    } else {
      result = Yup.object({
        UserName: Yup.string()
          .required()
          .label(
            props.mobileTabletVersion !== undefined ? 'Email' : 'UserName',
          ),
        Password: Yup.string().required(),
        RememberMe: Yup.boolean().default(initial.RememberMe),
        Recaptcha: Yup.string().nullable().default(''),
      });
      return result;
    }
  }, [initial.RememberMe, loginBadAttempts, props.mobileTabletVersion, t]);
  const handleSubmit = value => {
    if (onSubmit !== undefined && onSubmit !== null) {
      onSubmit(value);
    }
  };
  const handleCheckboxChange = (value, event) => {
    setRememberMe(event.target.checked);
  };
  const showForgotPassword = appSettings?.ShowForgotPassword;
  /*IsModuleEnabled(
    appSettings,
    KnownModules.ForgotPassword,
  );*/
  return (
    <>
      <Formik
        innerRef={formRef}
        validationSchema={schema}
        initialValues={initial}
        validateOnMount={true}
        validateOnBlur={false}
        onSubmit={async (values, formikHelpers) => {
          formikHelpers.setSubmitting(true);
          handleSubmit(values);
        }}
      >
        {formik => {
          return (
            <>
              <StyledForm onSubmit={formik.handleSubmit} avoidStyles={true}>
                <FormLeftSection>
                  <FormFieldsSection>
                    <FormRow fullRow={true}>
                      <FormTextField
                        name="UserName"
                        label={t(translations.UserName)}
                        placeholder={t(translations.UserName)}
                        disabled={formik.isSubmitting || submitting}
                        autoComplete="off"
                        id="UserNameId"
                        fullWidth
                        autoFocus={true}
                      />
                    </FormRow>
                    <FormRow fullRow={true}>
                      <FormTextField
                        name="Password"
                        label={t(translations.Password)}
                        placeholder={t(translations.EnterPassCode)}
                        disabled={formik.isSubmitting || submitting}
                        autoComplete="off"
                        id="PasswordId"
                        type="Password"
                        fullWidth
                      />
                    </FormRow>
                    {loginBadAttempts >= 3 && (
                      <FormRow>
                        <FormRecaptcha name="Recaptcha" />
                      </FormRow>
                    )}
                    <FormRow fullRow={true}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        {showRememberMe && (
                          <BasicCheckBox
                            name={'RememberMe'}
                            id={'RememberMeId'}
                            checked={rememberMe}
                            onChange={handleCheckboxChange}
                            label={t(translations.RememberMe)}
                          />
                        )}
                        {showForgotPassword && (
                          <ButtonLink
                            id={'forgotpasswordId'}
                            href={toRootedURL(
                              '/Registration/ResetPassword.aspx',
                            )}
                          >
                            {t(translations.IForgotPassword)}
                          </ButtonLink>
                        )}
                        {/* <div style={{ width: '50%' }}>
                         
                        </div>
                        {showForgotPassword && (
                          <div style={{ width: '50%', float: 'right' }}>
                            <div style={{ float: 'right' }}>
                              <ButtonLink
                                href={toRootedURL(
                                  '/Registration/ResetPassword.aspx',
                                )}
                              >
                                {t(translations.IForgotPassword)}
                              </ButtonLink>
                            </div>
                          </div>
                        )} */}
                      </div>
                    </FormRow>

                    <FormRow fullRow={true}>
                      <Button
                        variant="main"
                        size="large"
                        id="up_sinInId"
                        fullWidth
                        /*onClick={() => {
                            //formik.submitForm();
                          }}*/
                        disabled={submitting}
                        type="submit"
                      >
                        {t(translations.SignIn) as string}
                      </Button>
                    </FormRow>
                    {SSOSubmit && (
                      <FormRow fullRow={true}>
                        <Button
                          variant="white"
                          size="large"
                          id={'up_ssoId'}
                          fullWidth
                          onClick={SSOSubmit}
                          disabled={submitting}
                        >
                          {t(translations.auth_ShibbolethLoginLinkText)}
                        </Button>
                      </FormRow>
                    )}
                  </FormFieldsSection>
                </FormLeftSection>
              </StyledForm>
            </>
          );
        }}
      </Formik>
    </>
  );
}
